import { useEffect, useState } from "react";
import api, { fmtSeconds, fmtClockTime } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Users, Building2, Clock, Activity, ArrowRight, Camera, LogIn, LogOut } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";

export default function AdminOverview() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [report, setReport] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/admin/users"),
      api.get("/admin/clients"),
      api.get("/reports/summary", { params: { range_days: 7 } }),
    ]).then(([u, c, r]) => {
      setUsers(u.data);
      setClients(c.data);
      setReport(r.data);
    }).catch(() => {});
  }, []);

  const employees = users.filter((u) => u.role === "employee");
  const totalHours = report ? (report.totals?.total || 0) / 3600 : 0;
  const productiveHours = report ? (report.totals?.productive || 0) / 3600 : 0;
  const ratio = totalHours > 0 ? Math.round((productiveHours / totalHours) * 100) : 0;

  const chartData = (report?.by_date || []).map((d) => ({
    date: d.date.slice(5),
    productive: +(d.productive / 3600).toFixed(2),
    idle: +(d.idle / 3600).toFixed(2),
  }));

  const KPI = [
    { label: "Employees", value: employees.length, icon: Users, link: "/admin/users" },
    { label: "Clients", value: clients.length, icon: Building2, link: "/admin/clients" },
    { label: "Hours / 7d", value: totalHours.toFixed(1), icon: Clock, suffix: "h" },
    { label: "Productive Ratio", value: ratio, icon: Activity, suffix: "%" },
  ];

  return (
    <AppShell>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-stone-500 mb-1">
              [ {user?.role === "super_admin" ? "Super Admin" : "Admin"} / Overview ]
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Command Center</h1>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-stone-200 border border-stone-200 mb-6">
          {KPI.map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.label} className="bg-white p-5 group hover:bg-stone-50 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-stone-500">{k.label}</div>
                  <Icon className="h-4 w-4 text-stone-400" />
                </div>
                <div className="font-mono-num text-4xl font-semibold tracking-tighter">
                  {k.value}
                  {k.suffix && <span className="text-xl text-stone-400 ml-1">{k.suffix}</span>}
                </div>
                {k.link && (
                  <Link to={k.link} className="mt-3 inline-flex items-center gap-1 text-[11px] tracking-[0.15em] uppercase font-medium text-[#1f4d2e] hover:gap-2 transition-elegant">
                    Manage <ArrowRight className="h-3 w-3" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        {/* Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-stone-200 border border-stone-200 mb-6">
          <div className="lg:col-span-2 bg-white p-6">
            <div className="text-xs tracking-[0.25em] uppercase font-bold mb-1">Hours by Day · Last 7 days</div>
            <div className="text-xs text-stone-500 mb-4">Productive vs Idle (hours)</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid stroke="#e5e5e5" strokeDasharray="0" vertical={false} />
                  <XAxis dataKey="date" stroke="#737373" fontSize={11} fontFamily="IBM Plex Mono" />
                  <YAxis stroke="#737373" fontSize={11} fontFamily="IBM Plex Mono" />
                  <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #e5e5e5", fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                  <Bar dataKey="productive" stackId="a" fill="#1f4d2e" name="Productive" />
                  <Bar dataKey="idle" stackId="a" fill="#d4d4d4" name="Idle" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white p-6">
            <div className="text-xs tracking-[0.25em] uppercase font-bold mb-3">Top Employees · 7d</div>
            <div className="space-y-2">
              {(report?.by_user || [])
                .slice()
                .sort((a, b) => b.productive - a.productive)
                .slice(0, 6)
                .map((u) => {
                  const max = Math.max(...(report.by_user || []).map((x) => x.productive), 1);
                  const w = (u.productive / max) * 100;
                  return (
                    <div key={u.user_id}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="truncate">{u.name || u.email}</span>
                        <span className="font-mono-num font-bold">{(u.productive / 3600).toFixed(1)}h</span>
                      </div>
                      <div className="h-1.5 bg-stone-100"><div className="h-full bg-[#1f4d2e]" style={{ width: `${w}%` }} /></div>
                    </div>
                  );
                })}
              {(report?.by_user || []).length === 0 && (
                <div className="text-sm text-stone-500 text-center py-8">No data yet</div>
              )}
            </div>
          </div>
        </div>

        {/* Pie chart — productive vs idle (last 7 days) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="bg-white border border-stone-200 rounded-md p-6">
            <h3 className="font-display text-2xl font-medium text-[#1f4d2e]">Hours split</h3>
            <div className="font-display-italic text-sm text-stone-500 mb-4">Last 7 days · productive vs idle</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: "Productive", value: report?.totals?.productive || 0 },
                      { name: "Idle", value: report?.totals?.idle || 0 },
                    ]}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    innerRadius={56}
                    outerRadius={92}
                    paddingAngle={2}
                    stroke="#fdfcf7"
                    strokeWidth={3}
                  >
                    <Cell fill="#1f4d2e" />
                    <Cell fill="#d4a82a" />
                  </Pie>
                  <Tooltip
                    formatter={(v) => `${(v / 3600).toFixed(2)} h`}
                    contentStyle={{ borderRadius: 6, border: "1px solid #e7e5e4", fontFamily: "Inter", fontSize: 12 }}
                  />
                  <Legend iconType="square" wrapperStyle={{ fontFamily: "Inter", fontSize: 12, paddingTop: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="lg:col-span-2 bg-white border border-stone-200 rounded-md p-6">
            <h3 className="font-display text-2xl font-medium text-[#1f4d2e]">Per-employee productive hours</h3>
            <div className="font-display-italic text-sm text-stone-500 mb-4">Who delivered most in the last 7 days</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={(report?.by_user || []).map((u) => ({
                      name: u.name || u.email || "Unknown",
                      value: u.productive || 0,
                    }))}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={92}
                    paddingAngle={2}
                    stroke="#fdfcf7"
                    strokeWidth={3}
                  >
                    {(report?.by_user || []).map((_, i) => (
                      <Cell key={i} fill={["#1f4d2e", "#d4a82a", "#a8421f", "#3f6c4f", "#b8902a", "#7c2f15"][i % 6]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v) => `${(v / 3600).toFixed(2)} h`}
                    contentStyle={{ borderRadius: 6, border: "1px solid #e7e5e4", fontFamily: "Inter", fontSize: 12 }}
                  />
                  <Legend iconType="square" wrapperStyle={{ fontFamily: "Inter", fontSize: 12, paddingTop: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Quick Sessions list */}
        <div className="border border-stone-200">
          <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-stone-50">
            <div className="text-xs tracking-[0.25em] uppercase font-bold">Recent Sessions</div>
            <Link to="/admin/reports" className="text-xs text-[#1f4d2e] font-bold tracking-wider uppercase hover:underline">All Reports →</Link>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white border-b border-stone-200 text-[10px] tracking-[0.2em] uppercase text-stone-500">
              <tr>
                <th className="text-left px-5 py-3 font-bold">Date</th>
                <th className="text-left px-5 py-3 font-bold">Employee</th>
                <th className="text-left px-5 py-3 font-bold"><span className="inline-flex items-center gap-1.5"><LogIn className="h-3 w-3" />Time In</span></th>
                <th className="text-left px-5 py-3 font-bold"><span className="inline-flex items-center gap-1.5"><LogOut className="h-3 w-3" />Time Out</span></th>
                <th className="text-right px-5 py-3 font-bold">Productive</th>
                <th className="text-right px-5 py-3 font-bold">Idle</th>
                <th className="text-right px-5 py-3 font-bold">Total</th>
              </tr>
            </thead>
            <tbody>
              {(report?.sessions || []).slice(0, 10).map((s) => {
                const u = users.find((x) => x.id === s.user_id);
                return (
                  <tr key={s.id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-5 py-3 font-mono-num">{s.date}</td>
                    <td className="px-5 py-3">{u?.name || s.user_id.slice(0, 8)}</td>
                    <td className="px-5 py-3 font-mono-num text-[#1f4d2e]">{fmtClockTime(s.first_start_at)}</td>
                    <td className="px-5 py-3 font-mono-num text-[#a8421f]">
                      {s.is_running ? <span className="text-stone-400">running…</span> : fmtClockTime(s.last_stop_at)}
                    </td>
                    <td className="px-5 py-3 text-right font-mono-num text-[#1f4d2e] font-bold">{fmtSeconds(s.productive_seconds)}</td>
                    <td className="px-5 py-3 text-right font-mono-num text-stone-500">{fmtSeconds(s.idle_seconds)}</td>
                    <td className="px-5 py-3 text-right font-mono-num font-bold">{fmtSeconds(s.total_seconds)}</td>
                  </tr>
                );
              })}
              {(report?.sessions || []).length === 0 && (
                <tr><td colSpan="7" className="text-center py-12 text-sm text-stone-500">No sessions in the last 7 days.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
